// Shared lightweight "X is typing…" plumbing over the existing chat WebSocket.
// Used by the Client Portal chats, the internal chat and the project sidecar so
// every surface behaves identically.
import React, { useCallback, useRef, useState } from "react";
import logger from "@/lib/logger";

export function useChatTyping(wsRef, currentUserId) {
  const [typingUsers, setTypingUsers] = useState({});
  const timers = useRef({});
  const lastSent = useRef(0);
  const chanRef = useRef(null);
  const setChannel = useCallback((cid) => {
    if (chanRef.current !== cid) {
      chanRef.current = cid;
      setTypingUsers({});
      Object.values(timers.current).forEach(clearTimeout);
      timers.current = {};
    }
  }, []);
  const handleWsMsg = useCallback((msg) => {
    if (!msg || msg.type !== "typing") return;
    if (msg.channel_id !== chanRef.current || msg.sender_id === currentUserId) return;
    const sid = msg.sender_id;
    if (timers.current[sid]) clearTimeout(timers.current[sid]);
    if (msg.stop) { setTypingUsers((p) => { const n = { ...p }; delete n[sid]; return n; }); return; }
    setTypingUsers((p) => ({ ...p, [sid]: msg.sender_name || "Someone" }));
    timers.current[sid] = setTimeout(() => {
      setTypingUsers((p) => { const n = { ...p }; delete n[sid]; return n; });
      delete timers.current[sid];
    }, 4000);
  }, [currentUserId]);
  const clearSender = useCallback((sid) => {
    if (timers.current[sid]) { clearTimeout(timers.current[sid]); delete timers.current[sid]; }
    setTypingUsers((p) => { const n = { ...p }; delete n[sid]; return n; });
  }, []);
  const sendTyping = useCallback((has) => {
    const ws = wsRef.current; const cid = chanRef.current;
    if (!ws || ws.readyState !== 1 || !cid) return;
    const now = Date.now();
    if (has && now - lastSent.current < 2000) return;
    lastSent.current = now;
    try { ws.send(JSON.stringify({ type: "typing", channel_id: cid, stop: !has })); } catch (err) { logger.debug("typing send failed:", err?.message || err); }
  }, [wsRef]);
  return { typingUsers, setChannel, handleWsMsg, clearSender, sendTyping };
}

export function TypingLine({ typingUsers, testId = "typing-indicator", className = "px-4 pb-1 pt-0.5" }) {
  const names = Object.values(typingUsers);
  if (names.length === 0) return null;
  let label;
  if (names.length === 1) label = `${names[0]} is typing…`;
  else if (names.length === 2) label = `${names[0]} and ${names[1]} are typing…`;
  else label = `${names[0]}, ${names[1]} and ${names.length - 2} ${names.length - 2 === 1 ? "other" : "others"} are typing…`;
  return (
    <div className={`${className} flex items-center gap-1.5 text-xs text-slate-500`} data-testid={testId}>
      <span className="flex gap-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
      </span>
      <span className="italic truncate">{label}</span>
    </div>
  );
}
